'use client'

import { Card } from '@acko/card'
import { Badge } from '@acko/badge'
import { Button } from '@acko/button'
import { Typography } from '@acko/typography'
import { useState } from 'react'
import styles from '../page.module.css'
import { trackComprehensiveEngagement } from './ComprehensiveAnalyticsClient'

/* ─── Add-on data ────────────────────────────────────────────────────────────── */
type Addon = {
  id: string
  tag: string
  priceLabel: string
  title: string
  desc: string
  tip?: string
  /* Most common / recommended flag */
  popular?: boolean
}

const ADDONS: Addon[] = [
  {
    id: 'zero-dep',
    tag: 'Zero depreciation',
    priceLabel: '₹36/year',
    title: 'Bumper-to-bumper protection',
    desc: 'With this, you will be able to replace all damaged parts at the full cost, with no depreciation deduction. Without this, the insurers will deduct up to 50% from the cost of parts made from plastic or rubber, and you will have to make up the shortfall.',
    tip: "Expert tip: Plastic parts on a 3-year-old bike attract 30% depreciation. On a ₹40,000 fairing claim, that's ₹12,000 out of your pocket without zero dep.",
    popular: true,
  },
  {
    id: 'engine-protect',
    tag: 'Engine protect',
    priceLabel: '₹45/year',
    title: 'No more expensive repairs',
    desc: "It prevents hydrostatic lock and water ingress from damaging your engine, two of the biggest threats that a standard comprehensive bike policy doesn't cover.",
    tip: 'Expert tip: A single hydrostatic lock repair after riding through a flooded road costs ₹20,000–₹80,000. Engine protection covers this from just ₹45/year.',
    popular: true,
  },
  {
    id: 'personal-accident',
    tag: 'Personal accident',
    priceLabel: '₹350/year',
    title: 'Protection for what matters most',
    desc: 'Gets up to ₹15 lakhs if the bike owner is permanently disabled or dies in the accident. The Insurance Regulatory & Development Authority of India has mandated that two-wheeler policies include this requirement in their terms and conditions.',
    popular: true,
  },
  {
    id: 'return-to-invoice',
    tag: 'Return to invoice',
    priceLabel: '₹200/year',
    title: 'Get the full bike value back',
    desc: 'In case of total loss or confirmed theft, receive the full on-road invoice amount instead of the depreciated IDV. Especially valuable for new bikes in the first 2 years.',
  },
  {
    id: 'passenger-protect',
    tag: 'Passenger protect',
    priceLabel: '₹100/year',
    title: 'Cover for your pillion rider',
    desc: 'In case of an accident wherein the pillion passenger of your bike gets hurt seriously or dies, the policy will provide you with up to ₹2 lakh coverage, because in the owner policy, only the owner rider is considered entitled.',
  },
  {
    id: 'consumables',
    tag: 'Consumables cover',
    priceLabel: '₹120/year',
    title: 'Engine Oil & Filter coverage',
    desc: 'Will pay for the engine oil, oil filter, brake fluid, and coolant, which are replaced during the claim under bike insurance, which otherwise are not included in any standard policies.',
  },
]

/* ─── Add-on Card (Simple design matching main bike insurance page) ──────────── */
const AddonCard = ({ addon, className }: { addon: Addon; className: string }) => (
  <Card variant="primary" className={`p-20 md:p-24 lg:p-24 min-w-0 flex-col gap-12 ${className}`}>
    {/* Header row: badge + price (right-aligned) */}
    <div className="flex items-start justify-between gap-12">
      <div className="flex items-center gap-8 flex-wrap min-w-0">
        <Badge color="purple" variant="solid" size="md" textCase="sentence">
          {addon.tag}
        </Badge>
        {addon.popular && (
          <Badge color="green" variant="solid" size="md" textCase="sentence">
            Popular
          </Badge>
        )}
      </div>
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-8 items-end shrink-0">
        <Typography scale="base" color="secondary">
          starting at
        </Typography>
        <Typography scale="xl" emphasis="medium" as="span" color="primary">
          {addon.priceLabel}
        </Typography>
      </div>
    </div>

    <Typography scale="xl" emphasis="medium" as="h3" color="primary">
      {addon.title}
    </Typography>

    <Typography scale="base" color="secondary">
      {addon.desc}
    </Typography>

    {/* Expert tip */}
    {addon.tip && (
      <Card
        variant="ghost"
        className={`flex flex-col gap-12 p-20 md:mt-auto md:min-h-[145px] lg:!min-h-[103px] ${styles.tipWarningSurface}`}>
        <Typography scale="base" emphasis="medium" className={`text-wrap ${styles.tipTextWarning}`}>
          {addon.tip}
        </Typography>
      </Card>
    )}
  </Card>
)

/* ─── Main Section ───────────────────────────────────────────────────────────── */

export const ComprehensiveAddOnsSectionClient = () => {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <div id="comp-add-ons" className="flex flex-col gap-32 lg:gap-40 w-full scroll-mt-[104px]">
      {/* Section heading */}
      <div className="flex flex-col gap-8">
        <Typography scale="4xl" emphasis="bold" as="h2" color="primary" className="text-balance">
          Add-ons available with comprehensive insurance
        </Typography>
        <Typography scale="base" color="secondary">
          Choose from these valuable add-ons to enhance your comprehensive bike insurance coverage.
        </Typography>
      </div>

      <div className="flex flex-col gap-16 lg:gap-24 w-full">
        {/* Add-on grid: Mobile: 1 column, Tablet/Desktop: 2 columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 lg:gap-24 w-full">
          {ADDONS.map((addon, index) => {
            const visibilityClass = isExpanded ? 'flex' : index < 2 ? 'flex' : index < 4 ? 'hidden lg:flex' : 'hidden'

            return <AddonCard key={addon.id} addon={addon} className={visibilityClass} />
          })}
        </div>
        {ADDONS.length > 2 && (
          <div className="flex justify-center">
            <Button
              type="button"
              variant="secondary"
              size="lg"
              className="w-full lg:w-auto"
              onClick={() => {
                void trackComprehensiveEngagement(
                  'available_add_ons',
                  isExpanded ? 'Show less add-ons' : 'See all add-ons'
                )
                setIsExpanded(!isExpanded)
              }}>
              {isExpanded ? 'Show less add-ons' : 'See all add-ons'}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
